import { useEffect } from "react"
import type { PrimaryAction } from "../mapping/deriveFrameworkVerdict"

/** Editable surfaces the browser is already routing keys into — the same
 *  contract every "ignore while typing" shortcut in this codebase checks
 *  (form fields, and a contentEditable region for anything richer). The Excel
 *  grid counts too: it keeps its own keys, and lets Escape travel on only so
 *  dialogs can close (the old viewer's focused cell was contentEditable). */
function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable ||
    el.closest("[data-excel-editor]") !== null
  )
}

/** A Radix `Select` (`VerdictSelect`/`GeltungSelect`,
 *  since the native-`<select>` → Radix conversion) that owns the keydown
 *  itself — its trigger (`role="combobox"`) while merely focused, OR any
 *  item inside its open popup (`role="listbox"`/`role="option"`, portaled to
 *  `document.body`, so an ancestor walk from `e.target` is the only way to
 *  find it). Radix's own key handling (arrow-navigate the list, Enter/Space
 *  to pick, typeahead on a letter/digit, Escape to close) never calls
 *  `stopPropagation()` — only `preventDefault()` on some branches — so this
 *  hook's OWN window-level listener still sees every one of those keydowns
 *  bubble past it. Without this guard: an open dropdown's Enter double-fires
 *  this hook's primary action (the item's `role="option"` is not a
 *  BUTTON/link/`role="button"`, so `isFocusedInteractiveElement` misses it),
 *  its arrows ALSO cycle the active Verordnung underneath the popup, and its
 *  digits ALSO fire Übernehmen/Anfordern/Überspringen while the reviewer was
 *  only typing ahead to jump to an option. Matches the native `<select>`'s
 *  own precedent above (`isTypingTarget`'s SELECT branch): swallow every key
 *  the whole time focus sits anywhere in the control, open or closed — not
 *  just while its popup happens to be rendered — since a closed, focused
 *  Radix trigger reacts to ArrowUp/ArrowDown/Enter/Space itself (opens the
 *  popup) exactly as a focused native `<select>` did. */
function isSelectControlTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  return !!el.closest('[role="combobox"], [role="listbox"]')
}

/** An alert dialog — the approve confirmation (`ApproveConfirmDialog`) —
 *  owns every key while focus sits in it. Unlike `OpenQuestionsAckDialog`,
 *  whose textarea is already a typing target, it holds nothing but buttons,
 *  so with focus on its Cancel this hook's window-level listener still fired
 *  3 (Skip, navigating away), 2 (the request dialog on top), W and ? right
 *  underneath it. Only `alertdialog`, not `dialog`: every Radix popover in
 *  this workspace (help, open points, remap, supplier) wears `role="dialog"`
 *  too, and those stay non-blocking by design. */
function isAlertDialogTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  return !!el.closest('[role="alertdialog"]')
}

/** Focused controls the browser routes Enter/Space into for THEIR OWN
 *  activation — a BUTTON, a link, or anything wearing `role="button"` (this
 *  codebase's own pattern for a clickable non-button element, see e.g.
 *  `EntityListRow`, `AccordionHeader`, and this workspace's own
 *  `FrameworkRow`). Checked ONLY on the Enter branch below, not as a
 *  top-level guard like `isTypingTarget`: a plain BUTTON does not consume
 *  ArrowUp/Down/Left/Right, a digit, or `?` itself, so those must still
 *  reach this hook with focus parked on one — clicking a `FrameworkRow` to
 *  select it is exactly that (focus lands on the row's own BUTTON), and a
 *  top-level guard would have silently killed ↑↓/1/2/3/? for the rest of
 *  that click's session. Enter is different: a focused BUTTON/link/
 *  role="button" DOES consume it for its own click, so without this check a
 *  keyboard user tabbing to "Anwenden"/"Übernehmen" and pressing Enter got
 *  this hook's global primary action instead of the control they were
 *  actually looking at (the Enter branch below `preventDefault`s
 *  unconditionally, so the control's own activation never even ran). */
function isFocusedInteractiveElement(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el.tagName === "BUTTON" || el.tagName === "A") return true
  return el.getAttribute("role") === "button"
}

export interface StudioV2KeyboardActions {
  frameworks: string[]
  activeFramework: string | null
  onSelect: (framework: string) => void
  /** The declaration-level primary action — Enter on a single-Verordnung
   *  declaration fires whichever of the three this currently is. */
  primaryAction: PrimaryAction
  /** 1 — Übernehmen, gated identically to the footer button (a blocked call
   *  names its own reason rather than silently doing nothing). */
  onApprove: () => void
  /** 2 — Anfordern. Always available. */
  onRequest: () => void
  /** 3 — Überspringen. Always available. */
  onDefer: () => void
  /** Enter on a MULTI-Verordnung declaration: bundle-conform the active
   *  Verordnung, then advance to the next un-geprüft one (wrapping; a toast
   *  when none remain) — the caller owns both the bundling call and the
   *  advance, this hook only decides WHEN to fire it. */
  onConformActive: () => void
  /** ? — toggles the help popover. */
  onToggleHelp: () => void
  /** W — the guided layer's "Weiter": jump to the next open point. Standard
   *  behavior (TUR-1211); kept optional here so the key falls through
   *  untouched on any caller that omits it. ⌘W (close tab) is already
   *  covered by the shared modifier pass-through above the key branches. */
  onNext?: () => void
  /** Esc — back to the declarations list. */
  onEscape: () => void
  /** False on a terminal screen (the Done screen, or the post-Übernehmen
   *  Approved screen) — `StudioV2Layout` renders those in place of the whole
   *  workspace, but still calls this hook above that switch (hooks cannot be
   *  called conditionally), so without this flag Enter/1/2/3 kept firing real
   *  mutations (approve/request/defer, bundle-conform) against a declaration
   *  the reviewer has already left. Defaults to `true` so every existing
   *  caller (and every test that never mentions it) keeps behaving exactly
   *  as before. */
  enabled?: boolean
}

/**
 * The workspace's full keyboard scheme (B11): ↑↓ (←/→ as synonyms) cycles
 * the active Verordnung (B5); ↵ fires the primary action (single-Verordnung)
 * or bundles + advances (multi); 1/2/3 are Übernehmen/Anfordern/Überspringen;
 * ? toggles help; Esc leaves for the declarations list. Ignored while the
 * reviewer is typing anywhere on the page, same as every shortcut in this
 * codebase, and while `enabled` is false (a terminal screen — see its own
 * doc).
 *
 * EVERY key below only fires with no Cmd/Ctrl/Alt held — checked ONCE, first,
 * right after the typing-target guard (matches `DeclarationDocumentViewer`'s
 * own ordering). `⌘1`/`⌘2`/`⌘3` are pinned-tab shortcuts in most browsers,
 * `⌘↵`/`⌥↓` carry their own OS/browser meaning too, so a held modifier passes
 * every one of these keystrokes straight through untouched rather than firing
 * Übernehmen/Anfordern/Überspringen or stealing arrow navigation underneath it.
 */
export function useStudioV2Keyboard(i: StudioV2KeyboardActions): void {
  const {
    frameworks,
    activeFramework,
    onSelect,
    primaryAction,
    onApprove,
    onRequest,
    onDefer,
    onConformActive,
    onToggleHelp,
    onNext,
    onEscape,
    enabled = true,
  } = i
  useEffect(() => {
    if (!enabled) return

    const onKey = (e: KeyboardEvent) => {
      if (
        isTypingTarget(e.target) ||
        isSelectControlTarget(e.target) ||
        isAlertDialogTarget(e.target)
      )
        return

      // No modifier held — a held Cmd/Ctrl/Alt is a browser/OS chord (⌘1
      // switches tabs, ⌥↓ has its own OS meaning on some layouts) and must
      // pass through untouched. Checked FIRST, before every key branch below
      // (matches `DeclarationDocumentViewer`'s own ordering) — this used to
      // sit AFTER the Arrow/Enter/Escape branches, so ⌘+Enter fired Übernehmen
      // instead of the browser's own bookmark-bar toggle, and ⌥/⌘+Arrow stole
      // the OS's own word/tab navigation instead of passing it through.
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        // Shift+Arrow EXTENDS a text selection — the reviewer is selecting a
        // passage in the PDF to copy, not asking for another Verordnung. It
        // has to reach the browser untouched, so no preventDefault either
        // (fix P-g). Shift cannot join the top-level modifier guard above:
        // `?` is Shift+/ and `W` is Shift+w, both real shortcuts here.
        if (e.shiftKey) return
        if (frameworks.length === 0) return
        const idx = activeFramework ? frameworks.indexOf(activeFramework) : -1
        const delta = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1
        const next =
          frameworks[
            ((((idx === -1 ? 0 : idx) + delta) % frameworks.length) +
              frameworks.length) %
              frameworks.length
          ]
        e.preventDefault()
        onSelect(next)
        return
      }

      if (e.key === "Enter") {
        // A focused BUTTON/link/role="button" owns its OWN Enter activation
        // (e.g. a `FrameworkRow` just clicked into focus, or an "Anwenden"
        // button reached via Tab) — let it fire instead of hijacking Enter
        // for this hook's global primary action. See the function's own doc
        // for why this check lives HERE and not in the top-level guard.
        if (isFocusedInteractiveElement(e.target)) return
        e.preventDefault()
        if (frameworks.length <= 1) {
          if (primaryAction === "accept") onApprove()
          else if (primaryAction === "request") onRequest()
          else onDefer()
        } else {
          onConformActive()
        }
        return
      }

      if (e.key === "Escape") {
        onEscape()
        return
      }

      if (e.key === "1") {
        e.preventDefault()
        onApprove()
        return
      }
      if (e.key === "2") {
        e.preventDefault()
        onRequest()
        return
      }
      if (e.key === "3") {
        e.preventDefault()
        onDefer()
        return
      }
      if ((e.key === "w" || e.key === "W") && onNext) {
        e.preventDefault()
        onNext()
        return
      }
      if (e.key === "?") {
        e.preventDefault()
        onToggleHelp()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [
    enabled,
    frameworks,
    activeFramework,
    onSelect,
    primaryAction,
    onApprove,
    onRequest,
    onDefer,
    onConformActive,
    onToggleHelp,
    onNext,
    onEscape,
  ])
}
